import React, { useRef, useState } from "react";
import {
  Typography,
  Stack,
  Paper,
  Button,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import { Send, AttachFile, Close } from "@mui/icons-material";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";

// MessageInputContainer Component
const MessageInputContainer = ({
  newMessage,
  setNewMessage,
  onSendMessage,
  isAddingMessage,
}) => {
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const fileInputRef = useRef(null);

  const { handleUploadImage, state: uploadState } = useFolderWiseFileUploader();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Create preview URL for immediate display
        const previewUrl = URL.createObjectURL(file);
        setAttachmentPreview(previewUrl);
        setAttachment(file);

        // Upload file to AWS using the hook (same pattern as CreateTicket)
        const uploadResult = await handleUploadImage(
          false,
          { target: { files: [file] } },
          "withoutSession",
          "reception",
          "complaint-conversation"
        );

        if (uploadResult && uploadResult.fileURL) {
          setUploadedFileUrl(uploadResult.fileURL);
          console.log("File uploaded successfully:", uploadResult.fileURL);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        // Reset on error
        handleRemoveAttachment();
      }
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    setUploadedFileUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendWithAttachment = () => {
    // Send message with only the uploaded file URL as a string
    const messageData = {
      message: newMessage.trim(),
      attachment: uploadedFileUrl || null, // Just the URL string, not an object
    };

    console.log("Sending message with data:", messageData);
    onSendMessage(messageData);

    // Clear attachment after sending
    handleRemoveAttachment();
  };

  return (
    <Paper sx={{ p: 1, mt: 2, bgcolor: "#f9fafb" }}>
      {/* Attachment Preview */}
      {attachmentPreview && (
        <Box sx={{ mb: 1, position: "relative", display: "inline-block" }}>
          {attachment && attachment.type.startsWith("image/") ? (
            <img
              src={attachmentPreview}
              alt="Attachment preview"
              style={{
                maxWidth: "200px",
                maxHeight: "150px",
                borderRadius: "8px",
                border: "1px solid #ddd",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "200px",
                height: "80px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #ddd",
                borderRadius: "8px",
                bgcolor: "#f5f5f5",
                flexDirection: "column",
              }}
            >
              <AttachFile />
              <Typography variant="caption" sx={{ mt: 1 }}>
                {attachment?.name}
              </Typography>
            </Box>
          )}

          {/* Upload Status Indicator */}
          {uploadState.loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.8)",
                borderRadius: "8px",
              }}
            >
              <Typography variant="caption" color="primary">
                Uploading...
              </Typography>
            </Box>
          )}

          {uploadedFileUrl && (
            <Box
              sx={{
                position: "absolute",
                top: -5,
                left: -5,
                bgcolor: "success.main",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
              }}
            >
              ✓
            </Box>
          )}

          <IconButton
            size="small"
            onClick={handleRemoveAttachment}
            disabled={uploadState.loading}
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              bgcolor: "error.main",
              color: "white",
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        {/* Attachment Button */}
        <IconButton
          onClick={handleAttachmentClick}
          disabled={isAddingMessage || uploadState.loading}
         sx={{
            color: uploadState.loading ? "#9ca3af" : "#6366f1",
            bgcolor: "#f8faff",
            border: "2px solid #e0e7ff",
            borderRadius: "12px",
            width: 48,
            height: 48,
            "&:hover": { 
              bgcolor: "#e0e7ff",
              borderColor: "#6366f1",
              transform: "scale(1.05)"
            },
            "&:disabled": {
              bgcolor: "#f3f4f6",
              borderColor: "#e5e7eb"
            },
            transition: "all 0.2s ease"
          }}
        >
          <AttachFile />
        </IconButton>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="image/*,application/pdf"
          style={{ display: "none" }}
        />

        {/* Text Field with Right-to-Left Direction */}
        <TextField
          fullWidth
          multiline
          placeholder="Type your message here..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={isAddingMessage || uploadState.loading}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#f8fafc",
              border: "1px solid #e5e7eb",
              "&:hover": {
                borderColor: "#d1d5db",
              },
              "&.Mui-focused": {
                borderColor: "#8d8eecff",
                bgcolor: "white",
                boxShadow: "0 0 0 2px rgba(99, 102, 241, 0.1)"
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none"
              }
            },
            "& .MuiInputBase-input": {
              fontSize: "15px",
              lineHeight: 1.5,
              "&::placeholder": {
                color: "#9ca3af",
                opacity: 1
              }
            }
          }}
        />

        {/* Send Button */}
        <Button
          variant="contained"
          color="secondary"
          endIcon={<Send />}
          onClick={handleSendWithAttachment}
          disabled={
            isAddingMessage ||
            uploadState.loading ||
            (!newMessage.trim() && !uploadedFileUrl)
          }
          sx={{ minWidth: "100px" }}
        >
          {uploadState.loading ? "Uploading..." : "Send"}
        </Button>
      </Stack>
    </Paper>
  );
};

export default MessageInputContainer;
